import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
    FiUserPlus,
    FiSearch,
    FiX,
    FiTrash2,
    FiRefreshCw,
    FiUserCheck,
    FiEdit2
} from 'react-icons/fi';
import { getAllCoordinators, deleteCoordinator } from './coordinatorService';
import { getAllGuilds } from '../guilds/guildService';
import './CoordinatorList.css';

export default function CoordinatorList() {
    const [coordinators, setCoordinators] = useState([]);
    const [filteredCoordinators, setFilteredCoordinators] = useState([]);
    const [guilds, setGuilds] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [searchTerm, setSearchTerm] = useState('');

    const navigate = useNavigate();

    useEffect(() => {
        loadData();
    }, []);

    useEffect(() => {
        applyFilters();
    }, [coordinators, searchTerm]);

    async function loadData() {
        try {
            setLoading(true);
            const [coordinatorsData, guildsData] = await Promise.all([
                getAllCoordinators(),
                getAllGuilds(),
            ]);
            setCoordinators(coordinatorsData);
            setGuilds(guildsData);
            setError('');
        } catch (err) {
            setError('Failed to load data');
            console.error(err);
        } finally {
            setLoading(false);
        }
    }

    function applyFilters() {
        let filtered = [...coordinators];

        if (searchTerm) {
            const searchLower = searchTerm.toLowerCase();
            filtered = filtered.filter(c =>
                (c.fullName?.toLowerCase().includes(searchLower)) ||
                (c.email?.toLowerCase().includes(searchLower))
            );
        }

        setFilteredCoordinators(filtered);
    }

    async function handleDelete(id, event) {
        event.stopPropagation();
        if (!window.confirm('Are you sure you want to delete this coordinator?')) {
            return;
        }

        try {
            await deleteCoordinator(id);
            await loadData();
        } catch (err) {
            alert('Failed to delete coordinator');
            console.error(err);
        }
    }

    function handleEdit(id, event) {
        if (event) event.stopPropagation();
        navigate(`/dashboard/coordinators/edit/${id}`);
    }

    if (loading) {
        return (
            <div className="page-container">
                <div className="loading-spinner">
                    <FiRefreshCw className="spin-icon" />
                    <p>Loading coordinators...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="page-container">
            <div className="page-header">
                <div className="header-left">
                    <h1>Coordinators</h1>
                </div>
                <Link to="/dashboard/coordinators/create" className="btn-primary">
                    <FiUserPlus />
                    <span>Add Coordinator</span>
                </Link>
            </div>

            {error && (
                <div className="error-message">
                    <FiX />
                    {error}
                </div>
            )}

            {/* Filters Section */}
            <div className="filters-section">
                <div className="search-box">
                    <FiSearch className="search-icon" />
                    <input
                        type="text"
                        placeholder="Search by name or email..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
                {searchTerm && (
                    <button onClick={() => setSearchTerm('')} className="btn-clear-filters">
                        <FiX />
                        <span>Clear Filters</span>
                    </button>
                )}
            </div>

            <div className="table-container">
                <table className="data-table">
                    <thead>
                        <tr>
                            <th>Name</th>
                            <th>Email</th>
                            <th>Assigned Guilds</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredCoordinators.length === 0 ? (
                            <tr>
                                <td colSpan="4" className="empty-state">
                                    <div className="empty-content">
                                        <FiUserCheck className="empty-icon" />
                                        <p>
                                            {searchTerm
                                                ? 'No coordinators match your search.'
                                                : 'No coordinators found. Create your first coordinator.'}
                                        </p>
                                    </div>
                                </td>
                            </tr>
                        ) : (
                            filteredCoordinators.map((coordinator) => {
                                const assignedGuildNames = coordinator.assignedGuilds
                                    ?.map(id => guilds.find(g => g.id === id)?.name)
                                    .filter(Boolean)
                                    .join(', ') || 'None';

                                return (
                                    <tr key={coordinator.id}>
                                        <td className="name-cell">
                                            <strong>{coordinator.fullName || 'N/A'}</strong>
                                        </td>
                                        <td className="email-cell">{coordinator.email}</td>
                                        <td className="guilds-cell">
                                            <div className="guild-tags">
                                                {coordinator.assignedGuilds?.map(id => {
                                                    const name = guilds.find(g => g.id === id)?.name;
                                                    return name ? (
                                                        <span key={id} className="guild-tag">{name}</span>
                                                    ) : null;
                                                })}
                                                {(!coordinator.assignedGuilds || coordinator.assignedGuilds.length === 0) && 'None'}
                                            </div>
                                        </td>
                                        <td className="actions-cell">
                                            <button
                                                onClick={(e) => handleEdit(coordinator.id, e)}
                                                className="btn-edit"
                                                title="Edit coordinator"
                                            >
                                                <FiEdit2 />
                                            </button>
                                            <button
                                                onClick={(e) => handleDelete(coordinator.id, e)}
                                                className="btn-delete"
                                                title="Delete coordinator"
                                            >
                                                <FiTrash2 />
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
